import * as R from 'ramda'
import { watch } from 'vue'

import {
  getQueueIncidentUuid,
  IncidentQueueTimeoutError,
  isAddIncident,
  isAllowedQueueIncidentError,
  isUpdateIncident,
  QueueIncident,
} from '@collector/desktop-feature-incidents-queue-core'
import { isHttpError } from '@collector/shared-utils'
import { Incident } from '@collector/sportsapi-types'
import { sportsApiClient } from '@desktop/globalState/sportsApiClient'
import { queryClient } from '@desktop/queryClient'
import { useEvent, useIncidents } from '@desktop/views/Relation/composables'
import { injectEventIncidentsQueue } from '@desktop/views/Relation/provide-inject'
import { injectQueueIncidentErrorConfig } from '@desktop/views/Relation/Sports/provide-inject'
import {
  getApiIncidentUpdate,
  getAppIncident,
  getIncidentUpdate,
} from '@desktop/views/Relation/utils'

import { RabbitMQError } from '../../utils/queueRabbitClient'

export function useInitEventIncidentsQueue(): {
  initEventIncidentsQueue: () => void
} {
  // inject
  const eventIncidentsQueue = injectEventIncidentsQueue()
  const queueIncidentErrorConfig = injectQueueIncidentErrorConfig()

  // composables
  const { incidentsUpdate, incidents, incidentIdIncidentIndexMap } =
    useIncidents()

  const { event } = useEvent()

  // methods

  /**
   * Sets the `parent_id` field when processing `AddIncident` with parent references.
   * For example, Substitution In references the `id` of the Substitution Out incident as its `parent_id`.
   */
  function beforeQueueIncidentProcessing(queueIncident: QueueIncident): void {
    if (isAddIncident(queueIncident) && queueIncident.parentUuid) {
      const parentUuid = queueIncident.parentUuid

      const parentIncident = R.findLast(
        (incident) => incident.generated_id === parentUuid,
        incidents.value,
      )

      if (parentIncident) {
        queueIncident.payload.parent_id = parentIncident.id
      }
    }
  }

  /**
   * Removes queue incidents with error status if they exist in the incidents history
   */
  function cleanupErrorQueueIncidents(): void {
    // If the queue is not idle (currently processing),
    // all incidents including errored ones should be processed - do not remove error incidents
    if (eventIncidentsQueue.queueStatus !== 'idle') {
      return
    }

    const errorQueueIncidents = eventIncidentsQueue.getErrorQueueIncidents()
    if (errorQueueIncidents.length === 0) {
      return
    }

    const existingIncidentIds = new Set(
      incidents.value.map((incident) => incident.generated_id),
    )

    for (const queueIncident of errorQueueIncidents) {
      const queueIncidentUuid = getQueueIncidentUuid(queueIncident)

      if (queueIncidentUuid && existingIncidentIds.has(queueIncidentUuid)) {
        eventIncidentsQueue.dequeueIfExists(queueIncident)
      }
    }
  }

  /**
   * Removes queue incidents that have error status when their corresponding `uuid` receives confirmation from WebSocket
   */
  function watchQueueIncidentErrorTimeout(): void {
    watch(incidentsUpdate, (newVal) => {
      const updatedUuids = new Set(newVal.map((update) => update.uuid))

      for (const queueIncident of eventIncidentsQueue.getErrorQueueIncidents()) {
        const queueIncidentUuid = getQueueIncidentUuid(queueIncident)

        // Skip incidents without UUID (not confirmed by queue-rabbit-client)
        if (queueIncidentUuid && updatedUuids.has(queueIncidentUuid)) {
          eventIncidentsQueue.dequeueIfExists(queueIncident)
        }
      }
    })
  }

  /**
   * Fetches incidents from SportsAPI for the current event
   */
  async function fetchApiIncidents(): Promise<Incident[]> {
    const response = await sportsApiClient.Events.Incidents.get(
      event.value.id,
      {
        sort_type: 'id',
        sort_order: 'desc',
        limit: 50,
      },
    )
    return response.api.data.event_incidents
  }

  /**
   * Loads an incident from SportsAPI by filtering incidents using `generated_id` and adds it to the reactive `incidents` array.
   * Also updates the event data from the API to keep it synchronized.
   *
   * This function searches for an incident with the specified `uuid` in the API response and adds it to the local
   * `incidents` query cache if it doesn't already exist.
   */
  async function loadApiIncident(uuid: string): Promise<void> {
    const apiIncidents = await fetchApiIncidents()

    const apiIncident = apiIncidents.find(
      (incident) => incident.generated_id === uuid,
    )

    if (apiIncident) {
      const existingIncident = R.findLast(
        (incident) => incident.generated_id === uuid,
        incidents.value,
      )

      if (!existingIncident) {
        queryClient.setQueryData<Incident[]>(
          ['event', 'incidents', event.value.id],
          (prev = []) => [...prev, apiIncident],
        )

        queryClient.setQueryData(
          ['event', 'incidentsUpdate', event.value.id],
          [getApiIncidentUpdate(apiIncident)],
        )
      }
    }

    await queryClient.refetchQueries({
      queryKey: ['event', 'competition', event.value.id],
      exact: true,
    })
  }

  /**
   * Synchronizes a specific incident with the API by fetching the latest data and updating the local state.
   * Also updates the event data from the API to keep it synchronized.
   */
  async function syncApiIncident(id: Incident['id']): Promise<boolean> {
    const apiIncidents = await fetchApiIncidents()

    const apiIncident = apiIncidents.find((incident) => incident.id === id)

    if (apiIncident) {
      const appIncident = getAppIncident(
        apiIncident.id,
        incidents.value,
        incidentIdIncidentIndexMap.value,
      )

      // Only update if the API incident has a newer timestamp than the local incident
      if (appIncident && apiIncident.ut > appIncident.ut) {
        const newIncidentsUpdate = [
          getIncidentUpdate(appIncident, getApiIncidentUpdate(apiIncident)),
        ]

        queryClient.setQueryData<Incident[]>(
          ['event', 'incidents', event.value.id],
          (prev = []) =>
            prev.map((incident) =>
              incident.id === apiIncident.id ? apiIncident : incident,
            ),
        )

        queryClient.setQueryData(
          ['event', 'incidentsUpdate', event.value.id],
          newIncidentsUpdate,
        )

        await queryClient.refetchQueries({
          queryKey: ['event', 'competition', event.value.id],
          exact: true,
        })

        return true
      }
    }

    return false
  }

  /**
   * Handles errors that are allowed to occur during the processing of a queue incident.
   * @returns `true` if the error was handled and the queue incident can be removed from the queue
   */
  async function processAllowedQueueIncidentError(
    error: unknown,
    queueIncident: QueueIncident,
  ): Promise<boolean> {
    if (isHttpError(error, 409)) {
      const uuid = getQueueIncidentUuid(queueIncident)

      await loadApiIncident(uuid!)

      return true
    } else if (
      error instanceof IncidentQueueTimeoutError &&
      isUpdateIncident(queueIncident)
    ) {
      return await syncApiIncident(queueIncident.id)
    }

    // Handle incident already deleted by another process
    // This prevents queue errors when trying to delete an incident that was already deleted
    // by another process (e.g., parallel deletion attempts from different users)
    if (error instanceof RabbitMQError && [410, 423].includes(error.code)) {
      // Incident is already deleted - remove it from queue without showing error
      return true
    }

    if (!queueIncidentErrorConfig) {
      return false
    }

    return isAllowedQueueIncidentError(
      error,
      queueIncident,
      queueIncidentErrorConfig,
    )
  }

  function initEventIncidentsQueue(): void {
    cleanupErrorQueueIncidents()

    eventIncidentsQueue.setBeforeQueueIncidentProcessing(
      beforeQueueIncidentProcessing,
    )

    eventIncidentsQueue.setProcessAllowedQueueIncidentError(
      processAllowedQueueIncidentError,
    )

    /**
     * The `startProcessing` method must be executed after the specific sport configuration is loaded
     */
    if (eventIncidentsQueue.queueStatus === 'processing') {
      eventIncidentsQueue.startProcessing(false)
    }

    watchQueueIncidentErrorTimeout()
  }

  return {
    initEventIncidentsQueue,
  }
}
